<template>
    <main class="relative min-h-screen bg-white md:bg-gray-6 md:px-3">
        <div v-if="$store.getters.isInDevMode" class="fixed top-0 w-full bg-yellow-25 left-0 text-center z-[99]">In Dev mode</div>
        <LinkError v-if="$store.getters.hasLinkError"/>
        <div class="w-full h-full fixed bg-white top-0 left-0 z-50 flex justify-center items-center" v-if="isLoading" data-qa="page-loading">
            <Spinner class="mb-6 w-[80px] h-[80px]"/>
        </div>
        <div v-else class="relative">
            <div class="max-w-8xl mx-auto">
                <div class="w-full p-5 md:px-7 pt-7 md:pt-12 pb-4 mb-5">
                    <KeapLogo />
                </div>
                <ProgressBar class="mb-[90px]" />
                <div class="flex justify-center" v-if="shouldShowBillingToggle && !$store.getters.orderComplete">
                    <BillingToggle class="md:hidden" data-qa="mobile-billing-toggle" />
                </div>
            </div>
            <MobileCartSummary class="md:hidden" v-if="!$store.getters.orderComplete && isLoaded" @view-full-summary="$refs.summary.$el.scrollIntoView()"/>
            <div class="flex flex-col md:flex-row gap-7 mx-auto max-w-[938px] pb-14 md:pb-36 z-10 relative" v-if="isLoaded && !$store.getters.orderComplete">
                <Transition name="next-step" mode="out-in" @after-enter="afterEnter()">
                    <Customize v-if="currentStep === 0" class="transition-filter duration-200 ease-in-out" :class="{'blur-sm': isSoftLoading}" data-qa="customization-view"/>
                    <Payment ref="paymentDetails" v-else class="" data-qa="payment-view"/>
                </Transition>
                <Summary
                    ref="summary"
                    class="self-start sticky top-7 transition-filter duration-200 ease-in-out"
                    :class="{'blur-sm': isSoftLoading}"
                    :has-billing-toggle="shouldShowBillingToggle"
                    has-legal
                    has-support
                    has-checkout
                    :has-currency="!$store.getters.isCuratedLink"
                    has-charge-today
                    @submit-payment="submitPayment"
                    data-qa="main-summary"
                />
            </div>
            <Transition name="next-step" mode="out-in" @after-enter="afterEnter()">
                <div class="flex flex-col md:flex-row gap-7 mx-auto max-w-[938px] pb-14 md:pb-36" v-if="$store.getters.orderComplete">
                    <ThankYou data-qa="oder-complete-view" />
                </div>
            </Transition>
            <!-- <LiveAccessibleArea /> -->
            <Submitting v-if="$store.getters.isSubmitting"/>
            <Maintenance v-if="$store.getters.isMaintenance"/>
        </div>
        <Errors />
        <InstallmentsAgreement ref="installmentModal" @agreement-accepted="handleAgreement"/>
    </main>
</template>

<script>
import KeapLogo from '@/../svg/logo.svg?component';
import Spinner from '@/BuyNow/Components/Spinner.vue';
import ProgressBar from '@/BuyNow/Components/ProgressBar.vue';
import Summary from '@/BuyNow/Components/Summary/Summary.vue';
import MobileCartSummary from '@/BuyNow/Components/Summary/Flyout.vue';
import KeapCheckbox from '@/Components/KeapCheckbox.vue';
import BillingToggle from '@/BuyNow/Components/BillingToggle.vue';
import Customize from '@/BuyNow/Pages/Customize.vue';
import Payment from '@/BuyNow/Pages/Payment.vue';
import ThankYou from '@/BuyNow/Pages/ThankYou.vue';
import LiveAccessibleArea from '@/BuyNow/Components/LiveAccessibleArea.vue';
import Errors from '@/BuyNow/Components/Modals/Errors.vue';
import InstallmentsAgreement from '@/BuyNow/Components/Modals/InstallmentsAgreement.vue';
import Submitting from '@/BuyNow/Components/Modals/Submitting.vue';
import LinkError from '@/BuyNow/Components/LinkErrors/Error.vue';
import Maintenance from '@/BuyNow/Components/Modals/Maintenance.vue';

import * as Analytics from '@/Util/reporting.js';

export default {
    components: {
        KeapLogo,
        Spinner,
        ProgressBar,
        Summary,
        BillingToggle,
        MobileCartSummary,
        KeapCheckbox,
        LiveAccessibleArea,
        Customize,
        Payment,
        ThankYou,
        Errors,
        InstallmentsAgreement,
        Submitting,
        LinkError,
        Maintenance,
    },
    methods: {
        async submitPayment() {
            window.Sentry.setUser(this.$store.getters.userInfo.email);

            Analytics.pushToDataLayer({
                event: 'click',
                attributes: {
                    section: 'Plan Overview',
                    action: 'Click button',
                    event_label: 'Pay now',
                },
            });

            if (this.$store.getters.shouldShowInstallmentsAgreement) {
                this.$refs.installmentModal.openModal();

                const userAgreed = await new Promise((resolve, reject) => {
                    this.handleAgreementResolve = resolve;
                });
                if (!userAgreed) {
                    this.$store.commit('setError', {
                        title: 'Agreement Required',
                        message: 'You must agree to the terms to continue.',
                        code: 'dynamicError',
                        active: true,
                    });
                    return;
                }
            }
            this.$store.commit('setSubmitting', true);
            await this.$refs.paymentDetails.tokenize();
            await this.$store.dispatch('submitPurchase');
        },
        handleAgreement(userAgreed) {
            if (this.handleAgreementResolve) {
                this.handleAgreementResolve(userAgreed);
            }
        },

        afterEnter() {
            window.scrollTo(0, 0);
        }
    },
    async mounted() {
        this.$store.commit('setInitialLoading', true);
        const query = new URLSearchParams(window.location.search);
        await this.$store.dispatch('getFeatureFlags');
        if (window.location.pathname === '/') {
            await this.$store.dispatch('loadStateFromQuery', query);
        } else {
            await this.$store.dispatch('loadStateFromDB');
        }
        await this.$store.dispatch('loadStateFromCookie');
        if (!this.$store.getters.hasLinkError) {
            // The user can no longer self upgrade but we should leave this code here since that may change in the future.
            // if (this.$store.getters.isSelfUpgrading) {
            //     await this.$store.dispatch('validateJwt');
            // }

            if (this.$store.getters.isPurchaseLink) {
                await this.$store.dispatch('validateOpportunity');
            }

            await this.$store.dispatch('fetchBundleDetails');
            await this.$store.dispatch('fetchPricing');

            if (this.$store.getters.isUpgrading) {
                await this.$store.dispatch('fetchSubscription');
            }

            if (this.$store.getters.isAddingSub) {
                await this.$store.dispatch('fetchCustomer');
            }

            await this.$store.dispatch('setTracking', query);
            await this.$store.dispatch('hydrate');
        }

        this.$store.commit('setInitialLoading', false);
    },
    computed: {
        shouldShowBillingToggle() {
            if (!this.$store.getters.isCuratedLink) {
                return !this.$store.getters.featureFlags.MKT27461
            }
            return this.$store.getters.hasBillingToggle || !this.$store.getters.isCuratedLink;
        },
        lineItems() {
            return [
                {
                    itemId: this.$store.getters.selectedPlanId,
                    quantity: 1,
                },
                ...this.$store.getters.cartInfo.selectedAddons,
                ...this.$store.getters.cartInfo.selectedCharges,
            ];
        },
        isLoaded() {
            return this.$store.getters.isLoaded;
        },
        currentStep: {
            get() {
                return this.$store.getters.currentStep;
            },
            set(step) {
                this.$store.commit('setCurrentStep', step);
            }
        },
        currency: {
            get() {
                return this.$store.getters.selectedCurrency;
            },
            set(value) {
                this.$store.commit('updateUserInfo', { currency: value })
            }
        },
        isLoading: {
            get() {
                return this.$store.getters.initialLoading;
            },
            set(isLoading) {
                this.$store.commit('setInitialLoading', isLoading);
            }
        },
        isSoftLoading: {
            get() {
                return this.$store.getters.isSoftLoading;
            },
            set(isLoading) {
                this.$store.commit('setSoftLoaded', isLoading);
            }
        },
        country() {
            return this.$store.getters.userInfo.country;
        },
        state() {
            return this.$store.getters.userInfo.state;
        },
        postalCode() {
            return this.$store.getters.userInfo.postalCode;
        },
    },
    watch: {
        isLoading() {
            if (this.isLoading) {
                document.body.classList.add('overflow-hidden');
            } else {
                document.body.classList.remove('overflow-hidden');
            }
        },
        async state() {
            if (this.country === 'US' && this.postalCode.trim().length >= 5) {
                await this.$store.dispatch('fetchEstimate');
            }
        },
        async currency() {
            if (this.isLoaded) {
                await this.$store.dispatch('fetchPricing');
                if (this.country === 'US' && this.postalCode.trim().length >= 5) {
                    await this.$store.dispatch('fetchEstimate');
                }
            }
        },
        async postalCode() {
            if (this.country === 'US' && this.postalCode.trim().length >= 5) {
                await this.$store.dispatch('fetchEstimate');
            }
        },
        async country() {
            if (this.postalCode.trim().length >= 5 && this.country === 'US') {
                await this.$store.dispatch('fetchEstimate');
            } else if (this.country != 'US' && this.isLoaded) {
                await this.$store.dispatch('fetchEstimate');
            }
        },
        async isLoaded(newVal, oldVal) {
            if (newVal) {
                if (this.$store.getters.featureFlags?.BN_PULL_TOTALS_FROM_CB_ESTIMATE) {
                    await this.$store.dispatch('fetchEstimate');
                } else if (this.country != 'US' && this.$store.getters.isCuratedLink) {
                    await this.$store.dispatch('fetchEstimate');
                }
            }
        }
    }
};
</script>

<style lang="postcss" scoped>


.next-step-enter-active,
.next-step-leave-active {
    @apply transition-opacity delay-75 ease-in-out;
}

.next-step-enter-from,
.next-step-leave-to {
    @apply opacity-0;
}
</style>
